import type { ReactNode } from 'react';

interface Option<T extends string> { value: T; label: string; icon?: ReactNode; }

interface Props<T extends string> {
  options: Option<T>[];
  value: T;
  onChange: (v: T) => void;
  fullWidth?: boolean;
  /** 'note' = amber-tinted track (composer message/note toggle); 'accent' = default green. */
  tone?: 'accent' | 'note';
  size?: 'sm' | 'md';
}

// Generic control backing: composer's message/note toggle, the relocated Sign
// control, and sidebar/internal-tab tab rows — one implementation instead of
// each screen hand-rolling its own pill row.
export default function SegmentedControl<T extends string>({
  options, value, onChange, fullWidth, tone = 'accent', size = 'md',
}: Props<T>) {
  const trackBg = tone === 'note' ? 'rgba(239,159,39,.14)' : 'var(--hover-strong)';
  const activeBg = tone === 'note' ? 'var(--amber)' : 'var(--accent)';
  const activeColor = tone === 'note' ? '#241A05' : 'var(--text-on-accent)';
  const pad = size === 'sm' ? '2px 8px' : '6px 10px';
  const fontSize = size === 'sm' ? '0.66rem' : '0.78rem';

  return (
    <div style={{
      display: 'flex', background: trackBg, borderRadius: 8, padding: 3,
      width: fullWidth ? '100%' : 'fit-content', gap: 2,
    }}>
      {options.map((opt) => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            onClick={() => { if (!active) onChange(opt.value); }}
            style={{
              flex: fullWidth ? 1 : undefined,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              background: active ? activeBg : 'transparent',
              color: active ? activeColor : 'var(--shell-muted)',
              border: 'none', borderRadius: 6, padding: pad,
              fontSize, fontWeight: 700, cursor: active ? 'default' : 'pointer',
              transition: 'background .12s, color .12s', whiteSpace: 'nowrap',
            }}
          >
            {opt.icon}
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
